import * as React from 'react';
import { ICoreContext } from '@msdyn365-commerce/core';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy';

import HSetning from './hsetning.json';

interface IDangerousGoodsProps {
    baseClassName: string;
    context: ICoreContext;
    resources: {
        safetyDataSheetLabel: string;
        declarationOfConformityLabel: string;
    },
    attributes: AttributeValue[] | undefined;
}

type HSetningType = {
    HSetningKode: string;
    HKodeBeskrivelse: string;
    Tittel?: string;
    GhsPictogramUrlList?: string[];
};

/** Trim and only return a valid URL, otherwise return undefined. */
function validateUrl(urlString: string | undefined): string | undefined {
    if (!urlString) {
        return undefined;
    }

    urlString = urlString.trim();

    try {
        const url = new URL(urlString);
        return url.protocol === 'http:' || url.protocol === 'https:' ? urlString : undefined;
    } catch (e) {
        return undefined;
    }
}

const DangerousGoods: React.FC<IDangerousGoodsProps> = ({
    baseClassName,
    context,
    resources,
    attributes
}) => {
    const hSetningskoder = attributes?.find(attribute => attribute.Name === 'H-setningskoder')?.TextValue
        ?.split('|')
        .map(code => HSetning.find(x => x.HSetningKode === code) as HSetningType | undefined)
        .filter(Boolean) as HSetningType[] | undefined;
    const urlSamsvarserklaering = validateUrl(attributes?.find(attribute => attribute.Name === 'URL Samsvarserklæring')?.TextValue);
    const urlSikkerhetsdatablad = validateUrl(attributes?.find(attribute => attribute.Name === 'URL Sikkerhetsdatablad')?.TextValue);

    if ((!hSetningskoder || !hSetningskoder.length) && !urlSamsvarserklaering && !urlSikkerhetsdatablad) {
        return null;
    }

    // Sort codes with images first
    hSetningskoder?.sort((a, b) => {
        if (a.GhsPictogramUrlList?.[0] && !b.GhsPictogramUrlList?.[0]) {
            return -1;
        } else if (!a.GhsPictogramUrlList?.[0] && b.GhsPictogramUrlList?.[0]) {
            return 1;
        } else {
            return 0;
        }
    });

    return (
        <div className={`${baseClassName}__dangerous-goods`}>
            {hSetningskoder?.map((hsetning, index) => {
                return (
                    <div key={hsetning?.HSetningKode} className={`${baseClassName}__dangerous-goods__item`} data-code={hsetning?.HSetningKode}>
                        {hsetning?.GhsPictogramUrlList?.[0] && (
                            <img
                                src={hsetning.GhsPictogramUrlList[0]}
                                alt={hsetning.HKodeBeskrivelse}
                                className={`${baseClassName}__dangerous-goods__pictogram`}
                            />
                        )}
                        <div className={`${baseClassName}__dangerous-goods__content`}>
                            {hsetning?.Tittel && (
                                <div className={`${baseClassName}__dangerous-goods__content__title`}>{hsetning?.Tittel}</div>
                            )}
                            <div>{hsetning?.HKodeBeskrivelse}</div>
                        </div>
                    </div>
                );
            })}
            {(urlSamsvarserklaering || urlSikkerhetsdatablad) && (
                <div className={`${baseClassName}__dangerous-goods__links`}>
                    {urlSikkerhetsdatablad && (
                        <a href={urlSikkerhetsdatablad} target="_blank" rel="noopener noreferrer">
                            {resources.safetyDataSheetLabel}
                        </a>
                    )}
                    {urlSamsvarserklaering && (
                        <a href={urlSamsvarserklaering} target="_blank" rel="noopener noreferrer">
                            {resources.declarationOfConformityLabel}
                        </a>
                    )}
                </div>
            )}
        </div>
    );
};

export default DangerousGoods;
